// api-service.ts
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
    BASE_API_URL,
    OBTAIN_TOKEN_URL,
    REFRESH_TOKEN_URL
} from "./utils/api-consts";
import router from "../router";

// Интерфейсы для ответов API (camelCase)
interface LoginResponse {
    accessToken: string;
    refreshToken: string;
}

interface RefreshTokenResponse {
    accessToken: string;
}

// Интерфейс для параметров входа
interface LoginParams {
    username: string;
    password: string;
}

// Создание экземпляра axios с базовыми настройками
const apiClient = axios.create({
    baseURL: BASE_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Функция для входа пользователя
export const login = async ({ username, password }: LoginParams): Promise<void> => {
    try {
        const response: AxiosResponse<LoginResponse> = await axios.post(OBTAIN_TOKEN_URL, { username, password });
        const { accessToken, refreshToken } = response.data;
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        apiClient.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
        await router.navigate('/documents/');
    } catch (error) {
        console.error('Ошибка входа', error);
        throw error;
    }
};

// Функция для обновления access токена
export const refreshAccessToken = async (): Promise<string> => {
    try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            await router.navigate('/login');
            throw new Error('Нет refresh токена');
        }
        const response: AxiosResponse<RefreshTokenResponse> = await axios.post(REFRESH_TOKEN_URL, { refreshToken: refreshToken });
        const { accessToken } = response.data;
        localStorage.setItem('access_token', accessToken);
        apiClient.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
        return accessToken;
    } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        console.error('Ошибка обновления токена', error);
        await router.navigate('/login');
        throw error;
    }
};

// Функция для выхода пользователя
export const logout = async (): Promise<void> => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete apiClient.defaults.headers['Authorization'];
    await router.navigate('/login');
};

// Интерсептор запросов для добавления Authorization заголовка
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (config.data instanceof FormData && config.headers) {
            delete config.headers['Content-Type'];
        }
        const token = localStorage.getItem('access_token');
        if (token && config.headers) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

// Интерсептор ответов для обработки ошибок 401 и обновления токена
apiClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const newAccessToken = await refreshAccessToken();
                if (originalRequest.headers) {
                    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                }
                return apiClient(originalRequest);
            } catch (err) {
                throw error;
            }
        }
        return Promise.reject(error);
    }
);

export default apiClient;
